import {
    Button,
    DialogContentText,
    DialogTitle,
    Icon
} from "@material-ui/core";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import moment from "moment";
import React, { Component } from "react";
import { Trans } from "react-i18next";
import styled from "styled-components";

import Analytics from "js/analytics";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import { t } from "js/i18n";
import { BeautifulDialog, ShowDialogAsync } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { dialogTheme } from "js/react/materialThemeOverrides";
import ChangePaymentMethodDialog from "js/react/views/Billing/dialogs/ChangePaymentMethodDialog";
import withStripe from "js/react/views/Billing/utils/withStripe";

import { PaddedDialogActions, PaddedDialogContent } from "./components/DialogComponents";

// @ts-ignore
import "css/billing.scss";

const AlertContainer = styled.div`
    color: red;
    font-size: 15px;
    text-transform: uppercase;
    display: inline-flex;
    align-items: center;
`;

const ManageAccountLink = (props: { children?: React.ReactNode }) => (
    <a
        href="https://support.beautiful.ai/hc/en-us/articles/360044483772-Update-Billing"
        target="_blank"
        rel="noreferrer"
    >
        {props.children}
    </a>
);

function PaymentIssueDialogMessage({ daysUntilDowngrade }: { daysUntilDowngrade: number }) {
    return (
        <Trans
            i18nKey="You have <bold>{{days}} days</bold> to update your payment information before your account is downgraded. Learn more about managing your account <manageAccountLink>here</manageAccountLink>."
            components={{
                bold: <b />,
                manageAccountLink: <ManageAccountLink />
            }}
            values={{ days: daysUntilDowngrade }}
        />
    );
}

class PaymentIssueDialog extends Component<{ onClose: () => void } & BillingControllerState> {
    state = {
        fetching: false
    };

    componentDidMount() {
        Analytics.Dunning.PaymentIssueDialogOpened();
    }

    handleDismiss = () => {
        const { onClose } = this.props;

        Analytics.Dunning.PaymentIssueDialogDismissed();
        onClose();
    }

    handleUpdatePaymentMethod = async () => {
        const { onClose } = this.props;

        Analytics.Dunning.PaymentIssueDialogUpdatePaymentMethodClicked();
        this.setState({ fetching: true });

        const success = await ShowDialogAsync(ChangePaymentMethodDialog);
        if (success) {
            Analytics.Dunning.PaymentIssueDialogPaymentMethodUpdated();
            onClose();
            return;
        }

        this.setState({ fetching: false });
    }

    render() {
        const { stripeData: { subscription } } = this.props;
        const { fetching } = this.state;

        const latestInvoice = subscription.latest_invoice;
        const chargeFailureMessage = latestInvoice?.charge?.failure_message;
        const latestInvoiceAmount = latestInvoice?.total;

        const nextInvoiceDate = moment.unix(subscription.current_period_end)
            .format("MMM DD, YYYY");

        const daysUntilDowngrade = moment.unix(subscription.current_period_start)
            .add(30, "days")
            .diff(moment(), "days");

        return (
            <MuiThemeProvider theme={dialogTheme}>
                <BeautifulDialog className="update_payment_form" closeDialog={this.handleDismiss}>
                    <FetchingClickShield visible={fetching} backgroundColor="white" />
                    <DialogTitle>
                        <AlertContainer>
                            <Icon>error</Icon>
                            <span style={{ paddingLeft: 10 }}>{t("Payment Failed")}</span>
                        </AlertContainer>
                        <div style={{ paddingTop: 20 }}>{t("Don't lose your work!")}</div>
                    </DialogTitle>
                    <PaddedDialogContent>
                        <DialogContentText>
                            {t("There was a problem with your payment method.")}
                        </DialogContentText>
                        <DialogContentText>
                            <PaymentIssueDialogMessage
                                daysUntilDowngrade={daysUntilDowngrade}
                            />
                        </DialogContentText>
                        {chargeFailureMessage && <DialogContentText>
                            <b>{t("Failure Reason")}:</b> {chargeFailureMessage}
                        </DialogContentText>}
                        {latestInvoiceAmount && <DialogContentText>
                            {t("You will be charged {{amount}} today. Your next bill will be due {{date}}.", {
                                amount: `$${latestInvoiceAmount / 100}`,
                                date: nextInvoiceDate
                            })}
                        </DialogContentText>}
                    </PaddedDialogContent>
                    <PaddedDialogActions>
                        <Button color="secondary" onClick={() => this.handleDismiss()}>
                            {t("Dismiss")}
                        </Button>
                        <Button
                            color="primary"
                            disabled={fetching}
                            onClick={() => this.handleUpdatePaymentMethod()}>
                            {t("Update Payment Method")}
                        </Button>
                    </PaddedDialogActions>
                </BeautifulDialog>
            </MuiThemeProvider>
        );
    }
}

export default withStripe(BillingController.withInitializedState(PaymentIssueDialog));
